import styles from './style.postcss';

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import {
  connect,
  testClass,
  PageCard,
  Tooltip,
} from 'omni-common-ui';
import CourseViewTable from 'components/CourseViewTable';
import {
  actions as UnitResourcesActions,
  accessors as UnitResourcesAccessors,
  ResourceTypes,
  ResourceLabels,
  ResourceStatus,
  ResourceColumnOrder,
  ResourceDisplayCategory,
  getResourceDisplayCategory,
} from 'data/UnitResources';
import { getMembersByGroup } from 'data/GroupMembers/accessors';

import grayCircle from 'assets/svgs/gray-circle.svg';
import greenTick from 'assets/svgs/green-tick.svg';
import yellowTick from 'assets/svgs/yellow-tick.svg';
import lateClock from 'assets/svgs/late-clock.svg';

const RESOURCE_TYPES = ResourceColumnOrder;

const FILTERS = [
  {
    key: 'all',
    label: 'All',
    types: RESOURCE_TYPES,
    tooltip: null,
  },
  {
    key: 'listening',
    label: 'Listening & Understanding',
    types: [ResourceTypes.StoryVideo, ResourceTypes.Songs],
    tooltip: 'Students occasionally watches story videos or song videos, which provide helpful ' +
      'listening input, but usage is not yet consistent.',
  },
  {
    key: 'reading',
    label: 'Reading & Speaking Confidence',
    types: [ResourceTypes.UnitReader, ResourceTypes.ChallengeReader],
    tooltip: 'Unit readers and recording tasks are used infrequently. As a result, opportunities ' +
      'to practice reading aloud and speaking at home are limited.',
  },
  {
    key: 'pronunciation',
    label: 'Pronunciation & Phonics',
    types: [ResourceTypes.PhonicsReader, ResourceTypes.PhonicsVideo],
    tooltip: 'Phonics videos and readers are available but not regularly used. These resources ' +
      'are especially helpful for improving pronunciation at this age.',
  },
  {
    key: 'vocabulary',
    label: 'Vocabulary & Creative Use',
    types: [
      ResourceTypes.VocabularyPlayPart1,
      ResourceTypes.VocabularyPlayPart2,
    ],
    tooltip: 'Vocabulary or creative project - style activities are rarely explored. ' +
      'These activities are designed to make word practice more fun and active.',
  },
];

class UnitResources extends Component {
  constructor(props) {
    super(props);
    this.state = { activeFilterKey: 'all' };
  }

  componentDidMount() {
    this._fetchIfNeeded(this.props);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.unitId !== this.props.unitId) {
      this._fetchIfNeeded(nextProps);
    }
  }

  _fetchIfNeeded(props) {
    const { groupId, unitId } = props;
    if (groupId && unitId) {
      props.fetchUnitResourcesIfNeeded(groupId, unitId);
    }
  }

  _getActiveFilter() {
    return FILTERS.find((filter) => filter.key === this.state.activeFilterKey) || FILTERS[0];
  }

  _getVisibleTypes() {
    const activeFilter = this._getActiveFilter();
    return RESOURCE_TYPES.filter((type) => activeFilter.types.indexOf(type) !== -1);
  }

  _onFilterClick(filterKey) {
    this.setState({ activeFilterKey: filterKey });
  }

  _renderFilterButtons() {
    return (
      <div className={styles.UnitResources_filters}>
        {FILTERS.map((filter) => {
          const isActive = filter.key === this.state.activeFilterKey;
          const buttonClass = isActive
            ? `${styles.UnitResources_filterBtn} ${styles.UnitResources_filterBtnActive}`
            : styles.UnitResources_filterBtn;
          const button = (
            <button
                type="button"
                className={buttonClass}
                onClick={() => this._onFilterClick(filter.key)}>
              {filter.label}
            </button>
          );

          if (! filter.tooltip) {
            return <span key={filter.key}>{button}</span>;
          }

          return (
            <Tooltip
                key={filter.key}
                text={filter.tooltip}
                placement="bottom"
                trigger={['hover']}
                className={styles.UnitResources_filterTooltip}
                overlayClassName={styles.UnitResources_filterTooltip_inner}>
              {button}
            </Tooltip>
          );
        })}
      </div>
    );
  }

  _renderDash() {
    return <span className={styles.UnitResources_dash}>&ndash;</span>;
  }

  _renderIcon(src, alt) {
    return <img className={styles.UnitResources_icon} src={src} alt={alt} />;
  }

  _renderSingleIconCell(resource) {
    switch (resource.status) {
      case ResourceStatus.Completed:
        return this._renderIcon(greenTick, 'Completed');
      case ResourceStatus.CompletedLate:
        return (
          <Tooltip text="Completed late" placement="top" trigger={['hover']}>
            <span className={styles.UnitResources_lateCell}>
              {this._renderIcon(lateClock, 'Completed late')}
              {this._renderIcon(yellowTick, 'Completed late')}
            </span>
          </Tooltip>
        );
      case ResourceStatus.NotStarted:
      default:
        return this._renderIcon(grayCircle, 'Not started');
    }
  }

  _renderProgressCell(resource) {
    const isComplete = resource.status === ResourceStatus.Completed ||
      resource.status === ResourceStatus.CompletedLate;
    let completionIcon = this._renderIcon(greenTick, 'Completed');
    if (resource.status === ResourceStatus.CompletedLate) {
      completionIcon = this._renderSingleIconCell(resource);
    }
    return (
      <span className={styles.UnitResources_progressCell}>
        <span className={styles.UnitResources_progress}>{resource.progress}</span>
        <span className={styles.UnitResources_progressTotal}>{`/${resource.totalProgress}`}</span>
        {isComplete && completionIcon}
      </span>
    );
  }

  _renderChallengeReaderCell(resource) {
    const isCompleted = resource.status === ResourceStatus.Completed ||
      resource.status === ResourceStatus.CompletedLate;
    const hasExtraCount = isCompleted && resource.extraCount > 0;
    const challengeCellClassName = hasExtraCount
      ? `${styles.UnitResources_challengeCell} ${styles.UnitResources_challengeCellWithExtra}`
      : styles.UnitResources_challengeCell;
    let completionIcon = this._renderIcon(greenTick, 'Completed');
    if (resource.status === ResourceStatus.CompletedLate) {
      completionIcon = this._renderSingleIconCell(resource);
    }
    const cell = (
      <span className={challengeCellClassName}>
        {hasExtraCount && (
          <span className={styles.UnitResources_extra}>{`+${resource.extraCount}`}</span>
        )}
        {isCompleted ? completionIcon : this._renderIcon(grayCircle, 'Not started')}
      </span>
    );

    return (
      <Tooltip
          className={styles.UnitResources_challengeTooltip}
          text={`Total ${resource.readCount}/${resource.totalBooks}`}
          placement="top"
          trigger={['hover']}>
        {cell}
      </Tooltip>
    );
  }

  _renderResourceCell(resourceType, resources) {
    const resource = resources && resources.get(resourceType);
    if (! resource) {
      return this._renderDash();
    }

    switch (getResourceDisplayCategory(resourceType)) {
      case ResourceDisplayCategory.Progress:
        return this._renderProgressCell(resource);
      case ResourceDisplayCategory.ChallengeReader:
        return this._renderChallengeReaderCell(resource);
      case ResourceDisplayCategory.SingleIcon:
      default:
        return this._renderSingleIconCell(resource);
    }
  }

  _renderTable(members, visibleTypes) {
    const rows = members ? members.toArray() : [];

    return (
      <CourseViewTable students={rows} onlyRowDivider>
        <CourseViewTable.Header>
          <CourseViewTable.Row>
            {visibleTypes.map((type) => (
              <CourseViewTable.Cell key={type} columnId={type} small>
                {ResourceLabels[type]}
              </CourseViewTable.Cell>
            ))}
          </CourseViewTable.Row>
        </CourseViewTable.Header>
        <CourseViewTable.Body>
          {(member, columnId) =>
            this._renderResourceCell(
              columnId,
              this.props.resourcesByStudentId[member.studentGeneralInfo.id]
            )}
        </CourseViewTable.Body>
      </CourseViewTable>
    );
  }

  render() {
    const { members, isLoading } = this.props;
    const visibleTypes = this._getVisibleTypes();
    const hasMembers = members && members.size > 0;

    return (
      <div className={`${styles.UnitResources} ${testClass('unit-resources')}`}>
        {this._renderFilterButtons()}
        {! hasMembers && ! isLoading ? (
          <PageCard>
            <PageCard.Content>
              <div className={styles.UnitResources_empty}>No Results.</div>
            </PageCard.Content>
          </PageCard>
        ) : (
          <PageCard>
            <PageCard.Content className={styles.UnitResources_content}>
              {this._renderTable(members, visibleTypes)}
            </PageCard.Content>
          </PageCard>
        )}
      </div>
    );
  }
}

UnitResources.propTypes = {
  groupId: PropTypes.string,
  unitId: PropTypes.string,
  members: PropTypes.object,
  resourcesByStudentId: PropTypes.object,
  isLoading: PropTypes.bool,
  fetchUnitResourcesIfNeeded: PropTypes.func.isRequired,
};

UnitResources.defaultProps = {
  resourcesByStudentId: {},
};

export function mapStateToProps(state, ownProps) {
  const { groupId, unitId } = ownProps.params;
  const members = getMembersByGroup(state, groupId);
  const isLoading = UnitResourcesAccessors.isLoadingUnitResources(state, groupId, unitId);
  const studentResources = UnitResourcesAccessors.getUnitResources(state, groupId, unitId);

  const resourcesByStudentId = {};
  studentResources.forEach((student) => {
    resourcesByStudentId[student.studentId] = student.resources;
  });

  return { groupId, unitId, members, resourcesByStudentId, isLoading };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchUnitResourcesIfNeeded: UnitResourcesActions.fetchUnitResourcesIfNeeded,
  }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(UnitResources);



// WEBPACK FOOTER //
// ./containers/Group/UnitResources/component.jsx